import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { formatPercent } from '../../lib/format';

export interface ProgressBarProps {
  /**
   * Ratio where 1 = 100%. May exceed 1 — the fill clamps at the track end.
   * `null` = no data (empty track, value renders as "—").
   */
  value: number | null;
  /**
   * Track scale as a ratio (default 1 = the track ends at 100%). Use e.g. 1.25
   * so a 75% target tick sits inside the track (utilization style).
   */
  max?: number;
  /** Optional target tick, as a ratio (e.g. 0.75 = tick at 75%). */
  target?: number;
  /**
   * 'threshold' (default) = green, amber ≥ 75%, red ≥ 100% — budget-burn style
   *   (more is worse).
   * 'utilization' = cool slate < 60%, amber < 75%, green in the 75–110% target
   *   band, striped red ≥ 110% — the polarity is inverted (too little is as bad
   *   as too much) so the ramp diverges in hue, not just in shade.
   * 'accent' = fixed accent blue (no judgement, magnitude only).
   */
  color?: 'threshold' | 'accent' | 'utilization';
  /** Bar height: sm = h-1.5, md = h-2. */
  size?: 'sm' | 'md';
  /**
   * Diagonal hatch on the fill: "this figure is still accumulating", used for a
   * period that has not finished yet. A solid bar means a settled number.
   */
  striped?: boolean;
  /**
   * Where to render the % figure: 'none' (bar only, default), 'right' of the
   * bar (compact table meter), or 'above' it (with optional `caption`).
   */
  valueLabel?: 'none' | 'right' | 'above';
  /** Right side of the above-row (e.g. "of 120.0 h"). Used with valueLabel="above". */
  caption?: ReactNode;
  className?: string;
  /** Extra classes for the track itself (e.g. a fixed width in table cells). */
  barClassName?: string;
  'aria-label'?: string;
}

function thresholdFill(ratio: number): string {
  if (ratio >= 1) return 'bg-status-late';
  if (ratio >= 0.75) return 'bg-warning';
  return 'bg-success';
}

function thresholdText(ratio: number): string {
  if (ratio >= 1) return 'text-status-late';
  if (ratio >= 0.75) return 'text-status-partial';
  return 'text-secondary';
}

// ---------------------------------------------------------------------------
// Utilization ramp (billable ÷ capacity) — shared with the team charts so the
// bars, the meters and the KPI copy always agree on what "good" looks like.
// ---------------------------------------------------------------------------

export type UtilizationBand = 'idle' | 'low' | 'target' | 'over';

export const UTILIZATION_BANDS = {
  /** below this, the person is under-booked */
  low: 0.6,
  /** the target floor */
  target: 0.75,
  /** above this, the person is over capacity */
  over: 1.1,
} as const;

export function utilizationBand(ratio: number): UtilizationBand {
  if (ratio >= UTILIZATION_BANDS.over) return 'over';
  if (ratio >= UTILIZATION_BANDS.target) return 'target';
  if (ratio >= UTILIZATION_BANDS.low) return 'low';
  return 'idle';
}

/**
 * Hex per band, for Recharts fills and legend swatches.
 *
 * The polarity is DIVERGING, so the hues have to diverge too. Under-booked used
 * to be #dc2626 and over-capacity #b91c1c — two reds a hair apart encoding two
 * opposite management actions ("go sell work" vs "hire / stop selling"). Idle is
 * now a cool desaturated slate ("nothing sold here") and **red is reserved
 * exclusively for over capacity**. Nothing else on the utilization ramp may be
 * red. Keep this map, `utilizationFillClass` and `utilizationTextClass` in
 * lockstep — the bar, the meter and the number must never disagree.
 */
export const UTILIZATION_BAND_HEX: Record<UtilizationBand, string> = {
  idle: '#5a6f83', // secondary-500 — cool slate
  low: '#f59e0b', // warning amber
  target: '#22c55e', // success green
  over: '#dc2626', // primary-600 — red, over capacity only
};

/** Darker variant of the same hue, for text/numbers that need AA contrast. */
export const UTILIZATION_BAND_TEXT_HEX: Record<UtilizationBand, string> = {
  idle: '#5a6f83',
  low: '#d97706',
  target: '#16a34a',
  over: '#dc2626',
};

export const UTILIZATION_BAND_LABEL: Record<UtilizationBand, string> = {
  idle: 'Under-booked',
  low: 'Below target',
  target: 'On target',
  over: 'Over capacity',
};

/** Diagonal hatch layered on the over-capacity fill (redundant with the hue). */
export const UTILIZATION_OVER_STRIPE =
  'bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.45)_0_3px,transparent_3px_7px)]';

export const utilizationFillClass: Record<UtilizationBand, string> = {
  idle: 'bg-secondary-500',
  low: 'bg-warning',
  target: 'bg-success',
  // Striped as well as red: the one band that means "stop" is doubly encoded.
  over: `bg-primary-600 ${UTILIZATION_OVER_STRIPE}`,
};

export const utilizationTextClass: Record<UtilizationBand, string> = {
  idle: 'text-secondary-500',
  low: 'text-status-partial',
  target: 'text-status-paid',
  over: 'text-primary-600',
};

/**
 * Shared progress meter for budget burn, support allowances, and utilization.
 * Replaces the per-feature BurnBar / UtilizationBar implementations.
 */
export function ProgressBar({
  value,
  max = 1,
  target,
  color = 'threshold',
  size = 'md',
  striped = false,
  valueLabel = 'none',
  caption,
  className,
  barClassName,
  'aria-label': ariaLabel,
}: ProgressBarProps) {
  const fillPct = value == null ? 0 : (Math.min(Math.max(value, 0), max) / max) * 100;
  const band = utilizationBand(value ?? 0);
  const fillColor =
    color === 'accent'
      ? 'bg-accent'
      : color === 'utilization'
        ? utilizationFillClass[band]
        : thresholdFill(value ?? 0);
  const valueText = value == null ? '—' : formatPercent(value, { decimals: 0 });

  const track = (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-full bg-grey-200',
        size === 'sm' ? 'h-1.5' : 'h-2',
        valueLabel === 'right' && 'shrink-0',
        barClassName,
        valueLabel === 'none' && className,
      )}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value == null ? undefined : Math.round(value * 100)}
      aria-label={ariaLabel}
    >
      <div
        className={cn(
          'h-full rounded-full transition-all duration-300',
          fillColor,
          striped && UTILIZATION_OVER_STRIPE,
        )}
        style={{ width: `${fillPct}%` }}
      />
      {target != null && (
        <span
          className={cn(
            'absolute inset-y-0',
            // The target has to be legible at a glance, not a 1px hairline.
            color === 'utilization' ? 'w-0.5 bg-secondary-900/70' : 'w-px bg-secondary-400',
          )}
          style={{ left: `${(target / max) * 100}%` }}
          title={`${formatPercent(target, { decimals: 0 })} target`}
          aria-hidden
        />
      )}
    </div>
  );

  if (valueLabel === 'right') {
    return (
      <span className={cn('inline-flex items-center justify-end gap-2', className)}>
        {track}
        <span
          className={cn(
            'num w-11 shrink-0 text-right text-body-sm font-semibold',
            value == null
              ? 'text-grey-400'
              : color === 'utilization'
                ? utilizationTextClass[band]
                : 'text-secondary',
          )}
        >
          {valueText}
        </span>
      </span>
    );
  }

  if (valueLabel === 'above') {
    return (
      <div className={className}>
        <div className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              'num text-xs font-bold',
              color === 'accent'
                ? 'text-secondary'
                : color === 'utilization'
                  ? value == null
                    ? 'text-grey-400'
                    : utilizationTextClass[band]
                  : thresholdText(value ?? 0),
            )}
          >
            {valueText}
          </span>
          {caption != null && <span className="num text-xs text-grey-500">{caption}</span>}
        </div>
        <div className="mt-1">{track}</div>
      </div>
    );
  }

  return track;
}
